// import components
import { Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import {
  StyledBoxFlexAlignCenterSpaceBetween,
  StyledBoxFlexColumnSmallGap
} from 'components/shared/Box/Box.styles';
import NoteForm from './components/NoteForm';
import NoteList from './components/NoteList';
import { StyledButtonIcon, StyledNoteContainerBox } from './Notes.styles';

// import hooks
import { useMemo, useState } from 'react';
import useGetEmployeeNotes from './hooks/useGetEmployeeNotes';

// import types
import { NotesProps, NotesTypeEnums } from './Notes.types';

// import utils
import { t } from 'i18next';
import { DutyLineNote } from 'RestAPIs/employees/hours-table/types';
import { NOTE_VARIANT } from './Notes.consts';
import { IconContainer } from 'components/shared/Header/Header.styles';
import useGetRequestNotes from 'components/flex-portal/FlexHistory/hooks/useGetRequestNotes';

export default function Notes({
  employeeId,
  type = NotesTypeEnums.EMPLOYEE_NOTE,
  noteData = null,
  dutyLineId,
  requestId,
  isEmployeePortal = false,
  isSavedByParent = false,
  onAddNote,
  onRemoveNote,
  size = 'default',
  noteVariant = NOTE_VARIANT.DEFAULT,
  showTooltip = false,
  isWebSocketEnabled = false
}: NotesProps) {
  const isCompact = size === 'compact';

  // only fetch employee notes if type is EMPLOYEE_NOTE
  const { data: employeeNotes = [], isFetching } = employeeId
    ? useGetEmployeeNotes(
        { employeeId },
        {
          enabled: type === NotesTypeEnums.EMPLOYEE_NOTE
        }
      )
    : { data: [], isFetching: false };

  const { data: requestNoteData = [], isFetching: isFetchingRequestNotes } =
    useGetRequestNotes(
      { requestId },
      {
        enabled: !!requestId && type === NotesTypeEnums.FLEX_MATCH_REQUEST
      }
    );

  // Note: we filter out private notes in the employee portal in the FE, this should be handled in the BE ideally
  const visibleNoteData = useMemo(() => {
    const filteredNoteData =
      type === NotesTypeEnums.DUTY_LINE
        ? (noteData ?? []).filter(
            (note): note is DutyLineNote =>
              'id' in note &&
              (!isEmployeePortal || !note.flags?.includes('PRIVATE'))
          )
        : undefined;

    return filteredNoteData;
  }, [noteData, type, isEmployeePortal]);

  const notes = useMemo(() => {
    if (type === NotesTypeEnums.EMPLOYEE_NOTE) return employeeNotes;
    if (type === NotesTypeEnums.FLEX_MATCH_REQUEST) return requestNoteData;
    return visibleNoteData ? visibleNoteData : [];
  }, [type, employeeNotes, requestNoteData, visibleNoteData, isEmployeePortal]);

  const [showNoteForm, setShowNoteForm] = useState(false);
  const handleToggleForm = () => setShowNoteForm((visible) => !visible);

  return (
    <StyledNoteContainerBox>
      <StyledBoxFlexColumnSmallGap>
        <StyledBoxFlexAlignCenterSpaceBetween isCompact={isCompact}>
          <Typography
            variant={isCompact ? 'body2' : 'h6'}
            component={isCompact ? 'p' : 'h3'}
            color={isCompact ? 'text.secondary' : 'text.primary'}
          >
            {t('notes')} ({notes?.length})
          </Typography>
          {!isEmployeePortal && (
            <StyledButtonIcon
              onClick={handleToggleForm}
              color="primary"
              variant={isCompact ? 'text' : 'outlined'}
              size={isCompact ? 'small' : 'medium'}
              isCompact={isCompact}
            >
              {showNoteForm ? (
                t('cancel')
              ) : (
                <>
                  <IconContainer iconSize={isCompact ? 'SMALL' : 'MEDIUM'}>
                    <Icon type="plus" fontSize="inherit" color="primary" />
                  </IconContainer>
                  {t('addNote')}
                </>
              )}
            </StyledButtonIcon>
          )}
        </StyledBoxFlexAlignCenterSpaceBetween>
        {showNoteForm && !isEmployeePortal && (
          <NoteForm
            employeeId={employeeId}
            setShowNoteForm={setShowNoteForm}
            dutyLineId={dutyLineId}
            type={type}
            isEmployeePortal={isEmployeePortal}
            isSavedByParent={isSavedByParent}
            onAddNote={onAddNote}
            noteVariant={noteVariant}
            isCompact={isCompact}
            requestId={requestId}
            isWebSocketEnabled={isWebSocketEnabled}
          />
        )}
      </StyledBoxFlexColumnSmallGap>
      {notes && (
        <NoteList
          notes={notes}
          type={type}
          isEmployeePortal={isEmployeePortal}
          isFetching={
            type === NotesTypeEnums.EMPLOYEE_NOTE ||
            type === NotesTypeEnums.FLEX_MATCH_REQUEST
              ? isFetching || isFetchingRequestNotes
              : false
          }
          isSavedByParent={isSavedByParent}
          onRemoveNote={onRemoveNote}
          noteVariant={noteVariant}
          isCompact={isCompact}
          showTooltip={showTooltip}
          isWebSocketEnabled={isWebSocketEnabled}
        />
      )}
    </StyledNoteContainerBox>
  );
}
